/**
 * QuantumFlowSync.AI - Enhanced Admin Layout
 * Dark mode admin layout with permission-based navigation
 */

import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Workflow, 
  Users, 
  Shield, 
  FileText,
  Activity, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Bell,
  Moon,
  Sun,
  Search,
  ChevronRight,
  Monitor,
  Lock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermission, checkPermission } from '../hooks/usePermission';

const EnhancedAdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { employee, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // Navigation items with permissions
  const navigation = [
    { 
      name: 'Dashboard', 
      href: '/admin/dashboard', 
      icon: LayoutDashboard, 
      permission: null 
    },
    { 
      name: 'Employees', 
      href: '/admin/employees', 
      icon: Users, 
      permission: { resource: 'employees', scope: '*', action: 'read' }
    },
    { 
      name: 'Roles & Permissions', 
      href: '/admin/roles', 
      icon: Shield, 
      permission: { resource: 'roles', scope: '*', action: 'read' }
    },
    { 
      name: 'Audit Logs', 
      href: '/admin/audit-logs', 
      icon: FileText, 
      permission: { resource: 'audit', scope: 'logs', action: 'read' }
    },
    { 
      name: 'Security', 
      href: '/admin/security', 
      icon: Activity, 
      permission: { resource: 'security', scope: 'dashboard', action: 'read' }
    },
    { 
      name: 'Sessions', 
      href: '/admin/sessions', 
      icon: Monitor, 
      permission: null 
    },
    { 
      name: 'Security Settings', 
      href: '/admin/security-settings', 
      icon: Lock, 
      permission: null 
    },
    { 
      name: 'API Documentation', 
      href: '/admin/api-docs', 
      icon: FileText, 
      permission: { resource: 'permissions', scope: '*', action: 'read' }
    },
    { 
      name: 'Workflows', 
      href: '/workflows', 
      icon: Workflow, 
      permission: { resource: 'workflows', scope: '*', action: 'read' }
    },
  ];

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  // Check if route is active
  const isActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  // Generate breadcrumbs
  const getBreadcrumbs = () => {
    const paths = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [];
    
    let currentPath = '';
    paths.forEach((path, index) => {
      currentPath += `/${path}`;
      const navItem = navigation.find(item => item.href === currentPath);
      breadcrumbs.push({
        name: navItem ? navItem.name : path.charAt(0).toUpperCase() + path.slice(1),
        href: currentPath,
        isLast: index === paths.length - 1
      });
    });
    
    return breadcrumbs;
  };

  // Check permission for navigation item
  const hasNavPermission = (permission) => {
    if (!permission) return true;
    const { resource, scope, action } = permission;
    return checkPermission(employee, resource, scope, action);
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        } border-r transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center justify-between h-16 px-6 border-b ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <Link to="/admin/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Workflow className="w-5 h-5 text-white" />
              </div>
              <span className={`font-semibold text-lg ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                FlowSync
              </span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className={`lg:hidden ${
                darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              // Check permission
              if (item.permission && !hasNavPermission(item.permission)) {
                return null;
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? darkMode
                        ? 'bg-indigo-600 text-white'
                        : 'bg-indigo-600 text-white'
                      : darkMode
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User Profile */}
          <div className={`p-4 border-t ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {employee?.name || 'Employee'}
                </p>
                <p className={`text-xs truncate ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {employee?.roles?.[0]?.display_name || 'Employee'}
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Link
                to="/admin/profile"
                className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                  darkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Profile
              </Link>
              <button
                onClick={handleLogout}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                  darkMode
                    ? 'bg-red-900 text-red-200 hover:bg-red-800'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-200 ${sidebarOpen ? 'lg:pl-64' : ''}`}>
        {/* Top Bar */}
        <header className={`sticky top-0 z-40 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        } border-b h-16 flex items-center px-6`}>
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`lg:hidden mr-4 ${
              darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Breadcrumbs */}
          <div className="flex-1">
            <nav className="flex items-center space-x-2" aria-label="Breadcrumb">
              {getBreadcrumbs().map((crumb, index) => (
                <React.Fragment key={crumb.href}>
                  {index > 0 && (
                    <ChevronRight className={`w-4 h-4 ${
                      darkMode ? 'text-gray-600' : 'text-gray-400'
                    }`} />
                  )}
                  {crumb.isLast ? (
                    <span className={`text-sm font-medium ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {crumb.name}
                    </span>
                  ) : (
                    <Link
                      to={crumb.href}
                      className={`text-sm ${
                        darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {crumb.name}
                    </Link>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-4">
            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className={`p-2 rounded-lg transition-colors ${
                darkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className={`p-2 rounded-lg transition-colors relative ${
                  darkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Bell className="w-5 h-5" />
                {notifications.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>
              
              {/* Notifications dropdown */}
              {showNotifications && (
                <div className={`absolute right-0 mt-2 w-80 rounded-lg shadow-lg ${
                  darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                } border`}>
                  <div className={`p-4 border-b ${
                    darkMode ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <h3 className={`font-semibold ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      Notifications
                    </h3>
                  </div>
                  <div className="p-4 text-center">
                    <p className={`text-sm ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      No new notifications
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Employee info */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="hidden md:block">
                <p className={`text-sm font-medium ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {employee?.name}
                </p>
                {employee?.roles && employee.roles.length > 0 && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    darkMode
                      ? 'bg-indigo-900 text-indigo-200'
                      : 'bg-indigo-100 text-indigo-800'
                  }`}>
                    {employee.roles[0].display_name}
                  </span>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className={`p-6 ${darkMode ? 'text-gray-100' : ''}`}>
          <Outlet />
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default EnhancedAdminLayout;
